import { useCallback, useEffect, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { FileDown, Inbox, Loader2, LogOut, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import api, { formatApiErrorDetail } from "@/lib/api";

const STATUSES = ["all", "new", "contacted", "closed"];
const KINDS = ["all", "enquiry", "career"];

const statusStyle = {
  new: "border-brand/50 bg-brand/10 text-brand",
  contacted: "border-volt/40 bg-volt/10 text-volt",
  closed: "border-white/15 bg-white/5 text-muted2",
};

const inputCls =
  "w-full rounded-lg border border-white/10 bg-surface px-4 py-3 text-sm text-white placeholder:text-dim outline-none transition-colors focus:border-brand";

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState("all");
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status, kind };
      if (q) params.q = q;
      const [list, st] = await Promise.all([
        api.get("/enquiries", { params }),
        api.get("/enquiries/stats"),
      ]);
      setEnquiries(list.data);
      setStats(st.data);
    } catch (e) {
      if (e.response?.status === 401) setUser(null);
      else toast.error("Failed to load enquiries.");
    } finally {
      setLoading(false);
    }
  }, [status, kind, q]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoggingIn(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      toast.success(`Welcome back, ${data.user.name}.`);
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // session gone either way
    }
    setUser(null);
    setEnquiries([]);
    setStats(null);
  };

  const markStatus = async (id, next) => {
    try {
      await api.patch(`/enquiries/${id}`, { status: next });
      toast.success(`Marked as ${next}.`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink px-4" data-testid="admin-login-page">
        <Seo title="Admin — Vigarbiz" siteName="Vigarbiz" description="Vigarbiz admin console." />
        <div className="w-full max-w-md">
          <div className="absolute inset-0 grid-bg opacity-30" />
          <form
            onSubmit={login}
            data-testid="admin-login-form"
            className="relative rounded-2xl border border-white/10 bg-surface p-8 sm:p-10"
          >
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Vigarbiz logo" className="h-10 w-auto" />
              <span className="font-display text-xl font-black uppercase tracking-tight text-white">Vigarbiz</span>
            </div>
            <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.25em] text-dim">Admin console</p>
            <div className="mt-8 space-y-4">
              <div>
                <label htmlFor="admin-email" className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.15em] text-muted2">Email</label>
                <input
                  id="admin-email"
                  type="email"
                  data-testid="admin-email-input"
                  className={inputCls}
                  placeholder="admin@vigarbiz.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="admin-password" className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.15em] text-muted2">Password</label>
                <input
                  id="admin-password"
                  type="password"
                  data-testid="admin-password-input"
                  className={inputCls}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>
            {loginError && (
              <p data-testid="admin-login-error" className="mt-4 text-sm text-red-400">{loginError}</p>
            )}
            <button
              type="submit"
              disabled={loggingIn}
              data-testid="admin-login-submit"
              className="mt-8 w-full rounded-full bg-brand py-3.5 font-display text-sm font-bold text-ink transition-colors duration-300 hover:bg-brand-hover active:scale-95 disabled:opacity-60"
            >
              {loggingIn ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink" data-testid="admin-dashboard">
      <Seo title="Admin — Vigarbiz" siteName="Vigarbiz" description="Vigarbiz admin console." />
      <header className="border-b border-white/10 bg-surface/60 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Vigarbiz logo" className="h-8 w-auto" />
            <span className="font-display text-lg font-black uppercase tracking-tight text-white">Vigarbiz Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden font-mono text-xs text-dim sm:block">{user.email}</span>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-muted2 transition-colors hover:border-brand hover:text-brand"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        {stats && (
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-5" data-testid="admin-stats">
            {[
              { label: "Total", value: stats.total },
              { label: "New", value: stats.new },
              { label: "Contacted", value: stats.contacted },
              { label: "Closed", value: stats.closed },
              { label: "Careers", value: stats.careers },
            ].map((s) => (
              <div key={s.label} className="rounded-2xl border border-white/10 bg-surface p-5">
                <p className="font-display text-3xl font-black text-white">{s.value}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-dim">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2" data-testid="admin-status-filters">
            {STATUSES.map((s) => (
              <button
                key={s}
                data-testid={`admin-filter-status-${s}`}
                onClick={() => setStatus(s)}
                className={`rounded-full border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${
                  status === s ? "border-brand bg-brand text-ink" : "border-white/15 text-muted2 hover:text-white"
                }`}
              >
                {s}
              </button>
            ))}
            <span className="mx-1 hidden w-px bg-white/10 lg:block" />
            {KINDS.map((k) => (
              <button
                key={k}
                data-testid={`admin-filter-kind-${k}`}
                onClick={() => setKind(k)}
                className={`rounded-full border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${
                  kind === k ? "border-volt bg-volt text-ink" : "border-white/15 text-muted2 hover:text-white"
                }`}
              >
                {k === "enquiry" ? "Projects" : k === "career" ? "Careers" : "All types"}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <div className="relative flex-1 lg:w-64">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-dim" />
              <input
                data-testid="admin-search-input"
                className={`${inputCls} pl-10`}
                placeholder="Search name, email, company…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <button
              onClick={load}
              data-testid="admin-refresh-button"
              className="inline-flex items-center justify-center rounded-lg border border-white/15 px-3.5 text-muted2 transition-colors hover:border-brand hover:text-brand"
              aria-label="Refresh"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        <div className="mt-8 space-y-4" data-testid="admin-enquiry-list">
          {enquiries.length === 0 && !loading && (
            <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-white/15 py-16 text-center">
              <Inbox className="h-8 w-8 text-dim" />
              <p className="text-sm text-muted2">No enquiries match these filters yet.</p>
            </div>
          )}
          {enquiries.map((enq) => (
            <div
              key={enq.id}
              data-testid={`admin-enquiry-${enq.id}`}
              className="rounded-2xl border border-white/10 bg-surface p-6"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="font-display text-lg font-bold text-white">{enq.name}</h3>
                    <span className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${statusStyle[enq.status] || statusStyle.new}`}>
                      {enq.status}
                    </span>
                    <span className="rounded-full border border-white/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-dim">
                      {enq.kind === "career" ? `Career · ${enq.role}` : enq.service}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-muted2">
                    {enq.email}
                    {enq.company ? ` · ${enq.company}` : ""}
                    {enq.budget ? ` · ${enq.budget}` : ""}
                  </p>
                </div>
                <span className="font-mono text-[11px] text-dim">
                  {formatDistanceToNow(new Date(enq.created_at), { addSuffix: true })}
                </span>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-white/80">{enq.message}</p>
              {enq.kind === "career" && enq.resume_filename && (
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${enq.id}/resume`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`admin-resume-${enq.id}`}
                  className="mt-4 inline-flex items-center gap-2 rounded-full border border-brand/40 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-brand transition-colors hover:bg-brand hover:text-ink"
                >
                  <FileDown className="h-3.5 w-3.5" /> {enq.resume_filename}
                </a>
              )}
              <div className="mt-5 flex flex-wrap gap-2">
                {enq.status !== "contacted" && (
                  <button
                    data-testid={`admin-mark-contacted-${enq.id}`}
                    onClick={() => markStatus(enq.id, "contacted")}
                    className="rounded-full border border-volt/40 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-volt transition-colors hover:bg-volt hover:text-ink"
                  >
                    Mark contacted
                  </button>
                )}
                {enq.status !== "closed" && (
                  <button
                    data-testid={`admin-mark-closed-${enq.id}`}
                    onClick={() => markStatus(enq.id, "closed")}
                    className="rounded-full border border-white/15 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-muted2 transition-colors hover:bg-white hover:text-ink"
                  >
                    Mark closed
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
